import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { LessonRun } from '../../services/courseRunsService';
import { courseRunsService } from '../../services/courseRunsService';
import { jobsService, JobRecord } from '../../services/jobsService';

export interface LessonContextValue {
  lessonRun: LessonRun | null;
  document: Record<string, unknown> | null;
  loading: boolean;
  error: string | null;
  refreshDocument: () => Promise<void>;
  refreshRun: () => Promise<void>;
  executeStage: (
    stageId: string,
    options?: { payload?: Record<string, unknown>; onUpdate?: (job: JobRecord) => void },
  ) => Promise<JobRecord>;
  isExecuting: boolean;
  selectedStageId: string | null;
  setSelectedStageId: (id: string | null) => void;
}

const LessonContext = createContext<LessonContextValue | null>(null);

export function useLessonContext(): LessonContextValue {
  const ctx = useContext(LessonContext);
  if (!ctx) {
    throw new Error('useLessonContext must be used within a LessonContextProvider');
  }
  return ctx;
}

interface LessonContextProviderProps {
  lessonRun: LessonRun;
  children: React.ReactNode;
}

export function LessonContextProvider({
  lessonRun: initialRun,
  children,
}: LessonContextProviderProps) {
  const [lessonRun, setLessonRun] = useState<LessonRun>(initialRun);
  const [document, setDocument] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>('lesson_setup');
  const [isExecuting, setIsExecuting] = useState(false);

  const currentRunId = useRef(initialRun.run_id);
  useEffect(() => {
    if (initialRun.run_id !== currentRunId.current) {
      currentRunId.current = initialRun.run_id;
      setLessonRun(initialRun);
      setSelectedStageId('lesson_setup');
    }
  }, [initialRun]);

  const refreshRun = useCallback(async () => {
    try {
      const fresh = await courseRunsService.getLessonRun(lessonRun.run_id);
      setLessonRun(fresh);
    } catch (e: unknown) {
      console.error('Failed to refresh lesson run:', e);
    }
  }, [lessonRun.run_id]);

  const refreshDocument = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const doc = await courseRunsService.getLessonDocument(lessonRun.run_id);
      setDocument(doc);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load lesson document';
      setError(msg);
      setDocument(null);
    } finally {
      setLoading(false);
    }
  }, [lessonRun.run_id]);

  const executeStage = useCallback(async (
    stageId: string,
    options?: { payload?: Record<string, unknown>; onUpdate?: (job: JobRecord) => void },
  ): Promise<JobRecord> => {
    setIsExecuting(true);
    try {
      const { job_id } = await courseRunsService.executeLessonStage(lessonRun.run_id, stageId);
      const finalJob = await jobsService.waitForCompletion(job_id, {
        onUpdate: options?.onUpdate,
      });
      await refreshRun();
      await refreshDocument();
      if (finalJob.status === 'failed') {
        throw new Error((finalJob as Record<string, unknown>).error as string || 'Stage execution failed');
      }
      return finalJob;
    } finally {
      setIsExecuting(false);
    }
  }, [lessonRun.run_id, refreshRun, refreshDocument]);

  useEffect(() => {
    void refreshDocument();
  }, [lessonRun.run_id, refreshDocument]);

  const value = useMemo<LessonContextValue>(
    () => ({
      lessonRun,
      document,
      loading,
      error,
      refreshDocument,
      refreshRun,
      executeStage,
      isExecuting,
      selectedStageId,
      setSelectedStageId,
    }),
    [lessonRun, document, loading, error, refreshDocument, refreshRun, executeStage, isExecuting, selectedStageId],
  );

  return (
    <LessonContext.Provider value={value}>
      {children}
    </LessonContext.Provider>
  );
}
